class WebSocketClient {
  constructor(url, token, onMessage, onError, onStatusChange) {
    this.url = url
    this.token = token
    this.onMessage = onMessage
    this.onError = onError
    this.onStatusChange = onStatusChange
    this.ws = null
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 10
    this.reconnectIntervals = [1000, 1000, 2000, 3000, 5000, 8000, 13000, 21000, 30000, 30000] // Fibonacci-like
    this.pingInterval = null
    this.pongTimeout = null
    this.lastPhaseData = {}
  }

  connect() {
    try {
      // Ensure WebSocket URL is properly formatted for production
      const wsUrl = this.url
      
      // Validate URL format
      if (!wsUrl || (!wsUrl.startsWith('ws://') && !wsUrl.startsWith('wss://'))) {
        throw new Error(`Invalid WebSocket URL: ${wsUrl}`)
      }
      
      console.log('Connecting to WebSocket:', wsUrl)
      
      // Backend doesn't require authentication for WebSocket connections
      // Connect directly without subprotocols or tokens
      this.ws = new WebSocket(wsUrl)
      
      this.ws.onopen = () => {
        console.log('WebSocket connected successfully to:', wsUrl)
        this.reconnectAttempts = 0
        this.onStatusChange('connected')
        this.startHeartbeat()
        
        // No authentication needed for WebSocket connections
        console.log('WebSocket ready to receive messages')
      }

      this.ws.onmessage = (event) => {
        try {
          console.log('🔥 Raw WebSocket message received:', event.data)
          const data = JSON.parse(event.data)
          console.log('🔥 Parsed WebSocket message:', data)
          
          // Handle pong messages
          if (data.type === 'pong') {
            this.handlePong()
            return
          }
          
          // Store last phase data for resume
          if (data.phase && data.payload) {
            this.lastPhaseData[data.phase] = data
          }
          
          this.onMessage(data)
        } catch (error) {
          console.error('WebSocket message parse error:', error)
          this.onError(error)
        }
      }

      this.ws.onerror = (error) => {
        console.error('WebSocket error connecting to:', wsUrl, error)
        // Serialize the error event for Redux
        const serializedError = {
          message: error?.message || 'WebSocket connection failed',
          type: error?.type || 'error',
          target: error?.target?.readyState || 'unknown',
          url: wsUrl
        }
        this.onError(serializedError)
      }

      this.ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason)
        this.onStatusChange('disconnected')
        this.stopHeartbeat()
        
        // Don't reconnect if closed intentionally (code 1000) or auth failed (4001)
        if (event.code !== 1000 && event.code !== 4001) {
          this.scheduleReconnect()
        }
      }
    } catch (error) {
      console.error('WebSocket connection error:', error)
      this.onError(error)
      this.scheduleReconnect()
    }
  }

  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached')
      this.onStatusChange('failed')
      return
    }

    const delay = this.reconnectIntervals[Math.min(this.reconnectAttempts, this.reconnectIntervals.length - 1)]
    this.reconnectAttempts++
    
    console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`)
    this.onStatusChange('reconnecting')
    
    setTimeout(() => {
      this.connect()
    }, delay)
  }

  startHeartbeat() {
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }))
        
        // Set pong timeout - longer timeout for research operations
        this.pongTimeout = setTimeout(() => {
          console.log('Pong timeout, closing connection')
          this.ws?.close()
        }, 45000) // 45s timeout to handle longer research operations
      }
    }, 120000) // 2 minute interval to reduce interruptions during research
  }

  handlePong() {
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout)
      this.pongTimeout = null
    }
  }

  stopHeartbeat() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval)
      this.pingInterval = null
    }
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout)
      this.pongTimeout = null
    }
  }

  send(data) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data))
    } else {
      console.error('WebSocket not connected')
    }
  }

  disconnect() {
    this.stopHeartbeat()
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect')
      this.ws = null
    }
  }

  getLastPhaseData() {
    return this.lastPhaseData
  }
}

export default WebSocketClient